import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateCompanyFromSiret } from '@/modules/core/companies/hooks/useCompanies';
import { opportunitiesService } from '../../services/opportunities.service';

interface CreateBuyerForOpportunityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunityId: number;
  suggestedName?: string;
}

const SIRET_REGEX = /^\d{14}$/;

export function CreateBuyerForOpportunityDialog({
  open,
  onOpenChange,
  opportunityId,
  suggestedName,
}: CreateBuyerForOpportunityDialogProps) {
  const [siret, setSiret] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const createFromSiret = useCreateCompanyFromSiret();
  const queryClient = useQueryClient();

  const normalized = siret.replace(/\s/g, '');
  const isValid = SIRET_REGEX.test(normalized);
  const isPending = createFromSiret.isPending || isLinking;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error('Le SIRET doit contenir 14 chiffres');
      return;
    }

    try {
      const company = await createFromSiret.mutateAsync({
        siret: normalized,
        companyType: 'prospect',
      });

      setIsLinking(true);
      await opportunitiesService.update(opportunityId, { buyerId: company.id });

      toast.success(`Société "${company.name}" créée et liée à l'opportunité`);
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setSiret('');
      onOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Erreur lors de la création du prospect';
      toast.error(message);
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isPending) onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-copper" />
            Créer la fiche prospect
          </DialogTitle>
          <DialogDescription>
            L'annonce ne contient pas le SIRET de l'acheteur. Saisir le SIRET
            pour créer la fiche prospect et la lier à cette opportunité.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {suggestedName && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <p className="text-xs text-muted-foreground mb-0.5">
                Nom indiqué dans l'annonce
              </p>
              <p className="font-medium">{suggestedName}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="buyer-siret">Numéro SIRET</Label>
            <Input
              id="buyer-siret"
              placeholder="Ex: 23590001600016"
              value={siret}
              onChange={(e) => setSiret(e.target.value)}
              maxLength={17}
              autoComplete="off"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              14 chiffres. Les infos (nom INSEE, adresse) seront récupérées automatiquement.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Créer et lier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
